/* eslint-disable jsx-a11y/no-autofocus */
import {
    Box,
    Button,
    ClickAwayListener,
    Divider,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MigrationLink from '@shared/components/MigrationLink/MigrationLink';
import { useFeatureFlags } from '@shared/hooks/useFeatureFlag';
import useMsalUser from '@shared/hooks/useMsalUser';
import useNavigateToSearch from '@shared/hooks/useNavigateToSearch';
import appLogger from '@shared/logging/appLogger';
import { GetAdobeNameForPath } from '@shared/utilities/helper/adobe';
import urlData from '@shared/utilities/routes';
import { useRef, useState } from 'react';

import CartIcon from './Icons/CartIcon';
import RightNavMobile from './Mobile/RightNavMobile';
import styles from './Navbar.module.scss';

export default function Navbar() {
    const theme = useTheme();
    const flags = useFeatureFlags();
    const wcsUrl = `${process.env.AUCTIONS_WCS_URL}`;

    const lgScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const mdScreen = useMediaQuery('(min-width:1050px)');
    const smScreen = useMediaQuery('(min-width:750px)');
    const mobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const goToSearch = useNavigateToSearch();

    const { signIn, signOut, isAuthenticated } = useMsalUser();
    // used to give proximity MUI menu renders near
    const anchorRef = useRef<HTMLButtonElement>(null);
    // used to give MUI menu visibility
    const [open, setOpen] = useState<boolean>(false);

    // two methods below are a modified version of https://codesandbox.io/s/hover-menu-material-ui-8ey7q?file=/src/Menu.js:1457-1481 - thanks Chris! :)
    const handleOpenDropdown = () => {
        setOpen(true);
    };

    const handleButtonClick = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const handleCloseDropdown = (event: React.MouseEvent<HTMLElement>) => {
        if (event.currentTarget.localName !== 'ul') {
            // determines  the bounding area/box of the dropdownmenu
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const element: any = document.getElementById('navigation-menu');
            if (!element) return;

            const dropDownMenu = element.children[2];
            const dropDownMenuBoundary = {
                left: dropDownMenu.offsetLeft,
                top: event.currentTarget.offsetTop + event.currentTarget.offsetHeight,
                right: dropDownMenu.offsetLeft + dropDownMenu.offsetHeight,
                bottom: dropDownMenu.offsetTop + dropDownMenu.offsetHeight,
            };

            // if we are in the boundary, don't close the dropdown
            if (
                event.clientX >= dropDownMenuBoundary.left &&
                event.clientX <= dropDownMenuBoundary.right &&
                event.clientY <= dropDownMenuBoundary.bottom &&
                event.clientY >= dropDownMenuBoundary.top
            ) {
                return;
            }
        }

        // close the menu if the mouse isn't within the boundary
        setOpen(false);
    };

    const login = async () => {
        signIn();
    };

    const logout = async () => {
        setOpen(false);
        signOut();
    };

    const MenuCarrot = () => {
        return (
            <svg
                className="mobile-hidden tablet-hidden"
                width="16px"
                height="16px"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true">
                <path d="M7 10l5 5 5-5z" fill="#fff"></path>
                <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
        );
    };

    const MENU_ID = 'navigation-menu';
    const DropdownMenu = () => {
        return (
            <Popper
                open={open}
                style={{ zIndex: 950 }}
                className={styles['drop-down-menu']}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                transition
                disablePortal>
                {({ TransitionProps }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: 'right top',
                        }}>
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList
                                    id={MENU_ID}
                                    role="menu"
                                    onMouseOver={handleOpenDropdown}
                                    onMouseLeave={handleCloseDropdown}>
                                    <MenuItem>
                                        <MigrationLink
                                            config={urlData.myAuctions}
                                            onClick={handleButtonClick}>
                                            My Auctions
                                        </MigrationLink>
                                    </MenuItem>
                                    <MenuItem>
                                        <MigrationLink
                                            config={urlData.savedSearches}
                                            onClick={handleButtonClick}>
                                            Saved Searches
                                        </MigrationLink>
                                    </MenuItem>
                                    <MenuItem>
                                        <MigrationLink
                                            config={urlData.myTransactions}
                                            onClick={handleButtonClick}>
                                            My Transactions
                                        </MigrationLink>
                                    </MenuItem>
                                    <MenuItem>
                                        <MigrationLink
                                            config={urlData.myList}
                                            onClick={handleButtonClick}>
                                            My List
                                        </MigrationLink>
                                    </MenuItem>
                                    <MenuItem>
                                        <MigrationLink
                                            config={urlData.myProfile}
                                            onClick={handleButtonClick}>
                                            My Profile
                                        </MigrationLink>
                                    </MenuItem>
                                    {flags?.brokerageShowTimedAuctions ? (
                                        <MenuItem>
                                            <MigrationLink
                                                config={{
                                                    legacyLink: null,
                                                    mfeLink: '/timed-auctions',
                                                    shouldUseMFE: true,
                                                    ariaLabel: 'Timed Auctions',
                                                }}
                                                onClick={handleButtonClick}>
                                                Timed Auctions
                                            </MigrationLink>
                                        </MenuItem>
                                    ) : null}
                                    <Divider />
                                    <MenuItem onClick={logout} className={styles['sign-out']}>
                                        Sign Out
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        );
    };

    return mobileScreen ? (
        <RightNavMobile />
    ) : (
        <nav
            role="navigation"
            aria-label="main"
            className={styles['nav-bar']}
            onClickCapture={e => {
                const targetElement = e.target as HTMLElement;
                if (
                    ['a', 'hzn-text-link', 'hzn-button'].indexOf(
                        targetElement.tagName.toLowerCase()
                    ) >= 0
                ) {
                    appLogger.logEvent({
                        name: 'Click',
                        properties: {
                            linkOriginatingPageName: GetAdobeNameForPath(),
                            linkText: targetElement?.textContent,
                            linkPosition: 'Site Header',
                        },
                    });
                }
            }}>
            {lgScreen && (
                <>
                    <MigrationLink config={urlData.about}>ABOUT US</MigrationLink>
                    <MigrationLink config={urlData.locations}>
                        LOCATIONS &amp; SCHEDULES
                    </MigrationLink>
                    <MigrationLink config={urlData.faq}>FAQ</MigrationLink>
                </>
            )}
            {mdScreen && (
                <Box
                    sx={{
                        flex: '0 1 auto',
                        width: '128px',
                    }}></Box>
            )}
            {isAuthenticated && (
                <>
                    {smScreen && <MigrationLink config={urlData.checkin}>SIMULCAST</MigrationLink>}

                    {mdScreen && (
                        <>
                            <Button
                                variant="text"
                                ref={anchorRef}
                                onClick={handleButtonClick}
                                onMouseOver={handleOpenDropdown}
                                onMouseLeave={handleCloseDropdown}
                                aria-haspopup="true"
                                aria-controls={open ? MENU_ID : undefined}
                                aria-expanded={open ? 'true' : undefined}
                                sx={{ backgroundColor: '#00264f' }}>
                                MEMBER DASHBOARD
                                <MenuCarrot />
                            </Button>
                            <DropdownMenu />
                        </>
                    )}
                    <div className={styles['cta-button-wrap']}>
                        <hzn-button
                            compact={mobileScreen ? true : undefined}
                            onClick={goToSearch}
                            full-width
                            variant="cta">
                            search inventory
                        </hzn-button>
                    </div>
                    <CartIcon />
                </>
            )}
            {!isAuthenticated && (
                <>
                    {mdScreen && (
                        <>
                            <Button variant="text" href={`${wcsUrl}/register`}>
                                CREATE ACCOUNT
                            </Button>
                        </>
                    )}
                    <div style={{ minWidth: smScreen ? 236 : 128 }}>
                        <hzn-button
                            compact={mobileScreen ? true : undefined}
                            onClick={login}
                            full-width={true}
                            variant="cta">
                            sign in
                        </hzn-button>
                    </div>
                </>
            )}
        </nav>
    );
}
